/**
 * Controlled Select Component
 *
 * All-in-one component combining:
 * - React Hook Form Controller
 * - Ant Design Select
 * - FormFieldWrapper (label, required, error)
 *
 * Eliminates repetitive Controller + Select + label + error code.
 *
 * Usage:
 * <ControlledSelect
 *   name="category"
 *   control={control}
 *   label="Category"
 *   required
 *   options={[{ label: 'Option 1', value: '1' }]}
 *   rules={{ required: 'Category is required' }}
 * />
 */

import React from 'react';
import { Select } from 'antd';
import { Control, Controller, RegisterOptions, FieldValues, Path } from 'react-hook-form';
import { FormFieldWrapper } from './FormFieldWrapper';
import { useWizardStyles } from '@/styles/wizardStyles';

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export interface ControlledSelectProps<TFieldValues extends FieldValues = FieldValues> {
  /** Field name (must match form schema) */
  name: Path<TFieldValues>;

  /** React Hook Form control object */
  control: Control<TFieldValues>;

  /** Field label */
  label: string;

  /** Whether the field is required */
  required?: boolean;

  /** Validation rules */
  rules?: RegisterOptions<TFieldValues>;

  /** Select options */
  options: SelectOption[];

  /** Placeholder text */
  placeholder?: string;

  /** Help text to display below the select */
  help?: string;

  /** Whether the select is disabled */
  disabled?: boolean;

  /** Whether the select is read-only (prevents interaction but maintains normal visual style) */
  readOnly?: boolean;

  /** Whether the select is loading */
  loading?: boolean;

  /** Whether to show search input */
  showSearch?: boolean;

  /** Allow clear selection */
  allowClear?: boolean;

  /** Enable multiple selection */
  mode?: 'multiple' | 'tags';

  /** Filter option function for search */
  filterOption?: (input: string, option?: SelectOption) => boolean;

  /** Additional class name */
  className?: string;

  /** On change callback (in addition to form's onChange) */
  onChangeCallback?: (value: string | number | string[] | number[]) => void;
}

/**
 * Controlled Select Component
 *
 * Provides a fully integrated form select with validation, labels, and error handling.
 */
export function ControlledSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  required = false,
  rules,
  options,
  placeholder,
  help,
  disabled = false,
  readOnly = false,
  loading = false,
  showSearch = false,
  allowClear = true,
  mode,
  filterOption,
  className,
  onChangeCallback,
}: ControlledSelectProps<TFieldValues>) {
  const styles = useWizardStyles();

  // Default filter function for showSearch
  const defaultFilterOption = (input: string, option?: SelectOption) => {
    if (!option) return false;
    return option.label.toLowerCase().includes(input.toLowerCase());
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        // Normalize 0 to undefined so placeholder is shown instead of "0"
        // This is especially important for location IDs where 0 is invalid
        const normalizedValue = field.value === 0 ? undefined : field.value;

        return (
          <FormFieldWrapper
            label={label}
            required={required}
            error={error?.message}
            help={help}
            className={className}
          >
            <Select
              {...field}
              value={normalizedValue}
            size="large"
            placeholder={placeholder}
            disabled={disabled}
            loading={loading}
            showSearch={!readOnly && showSearch}
            allowClear={!readOnly && allowClear}
            mode={mode}
            options={options}
            optionFilterProp="label"
            filterOption={showSearch ? (filterOption || defaultFilterOption) : undefined}
            style={error ? styles.formInputError : styles.formInput}
            className={readOnly ? 'form-readonly-select' : undefined}
            status={error ? 'error' : ''}
            open={readOnly ? false : undefined}
            onChange={(value) => {
              if (readOnly) return;
              field.onChange(value);
              if (onChangeCallback && value != null) {
                onChangeCallback(value);
              }
            }}
            />
          </FormFieldWrapper>
        );
      }}
    />
  );
}

export default ControlledSelect;
